/* The app's icon system.

   Feather (react-icons/fi) is the single source for UI icons: 24px grid, 2px
   stroke, round caps. Mixing families is what makes an interface look assembled
   rather than designed — a solid Font Awesome glyph next to a Feather outline
   reads as two different weights at the same size.

   Feather is missing three marks we need, so they are drawn here on the same
   grid with the same stroke so they sit correctly beside the rest. Brand logos
   are the one deliberate exception: they have to stay on-brand, not on-grid. */

import React from "react";

/* Icon sizes. Anything between these steps reads as a mistake, not a nuance. */
const ICON = {
  xs: 12, // dense meta rows, inline with 11-12px text
  sm: 14, // default inline icon
  md: 16, // buttons, form fields
  lg: 20, // section headers
  xl: 24, // empty states, feature tiles
};

/* Feather's own geometry, so custom marks are indistinguishable from the set */
const GridIcon = ({ size = ICON.sm, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

/* The AI mark. Replaces three interchangeable sparkles that were in use —
   HiSparkles (solid), IoSparklesOutline and IoFlashOutline (two more weights). */
export const Spark = (props) => (
  <GridIcon {...props}>
    <path d="M10.5 3.5 12.2 8.3 17 10l-4.8 1.7-1.7 4.8-1.7-4.8L4 10l4.8-1.7Z" />
    <path d="M18 14.5v5M15.5 17h5" />
  </GridIcon>
);

/* Filled star for the saved/active state — Feather only ships the outline, and
   the filled counterpart was being borrowed from Font Awesome at a heavier weight */
export const StarFilled = ({ size = ICON.sm, ...rest }) => (
  <GridIcon size={size} fill="currentColor" {...rest}>
    <path d="M12 2.5 l2.95 5.98 l6.6 0.96 l-4.77 4.65 l1.12 6.57 L12 17.55 l-5.9 3.11 l1.13 -6.57 L2.45 9.44 l6.6 -0.96 Z" />
  </GridIcon>
);

/* Company mark. Feather has no building; this keeps the 2px stroke and the
   same 3px inset from the grid edge that Feather's framed icons use. */
export const Building = (props) => (
  <GridIcon {...props}>
    <path d="M3 21h18" />
    <path d="M5 21V4.5A1.5 1.5 0 0 1 6.5 3h6A1.5 1.5 0 0 1 14 4.5V21" />
    <path d="M14 9.5h3.5A1.5 1.5 0 0 1 19 11v10" />
    <path d="M8 7.5h2M8 11.5h2M8 15.5h2" />
  </GridIcon>
);

/* Feather has no trophy — FiAward is a rosette, which reads as a badge rather
   than a win. Drawn here with the same handles-and-plinth silhouette. */
export const Trophy = (props) => (
  <GridIcon {...props}>
    <path d="M6.5 4H4.5a2.5 2.5 0 0 0 0 5h2" />
    <path d="M17.5 4h2a2.5 2.5 0 0 1 0 5h-2" />
    <path d="M6.5 2.5h11V9a5.5 5.5 0 0 1-11 0Z" />
    <path d="M12 14.5V18" />
    <path d="M8.5 21.5h7a3.5 3.5 0 0 0-3.5-3.5 3.5 3.5 0 0 0-3.5 3.5Z" />
  </GridIcon>
);

/* Research: a display with a magnifier. The monitor's bottom edge stops short
   so the lens sits in the gap instead of crossing the frame. */
export const DeviceSearch = (props) => (
  <GridIcon {...props}>
    <path d="M21 11.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h6" />
    <path d="M11 16v4" />
    <path d="M9 20h4" />
    <circle cx="17.5" cy="17.5" r="3.5" />
    <path d="m20 20 2 2" />
  </GridIcon>
);

/* Brand logos keep their own geometry on purpose — a brand mark redrawn on a
   generic grid stops being recognisable, which is why these stay solid Font
   Awesome glyphs rather than being folded onto the Feather outline set. */
export { FaXTwitter as BrandX } from "react-icons/fa6";
export { FaLinkedin as BrandLinkedIn } from "react-icons/fa";
